import { useQuery } from '@apollo/client';
import GET_REVIEWS_GQL from 'api/queries/getFeedbacks';
import { Icon, ModalElement } from 'kl-b2c-ui-kit';
import React, { FC, createRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Slider, { Settings } from 'react-slick';
import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';
import { BaseWrapper } from 'styles/common';
import { Review as ReviewModel } from 'types';
import Review from './Review';
import {
    Action,
    ActionText,
    Container,
    Description,
    DescriptionContainer,
    DotsContainer,
    DotsList,
    Slide,
    SliderContainer,
    PersonalDataDisclaimer,
    Bubble9,
    Coral,
} from './styled';
import PrimaryModal from 'components/modal/PrimaryModal';
import DOMPurify from 'dompurify';
import { Bubble8 } from '../phases/styled';

interface ReviewsProps {
    config: {
        description: string;
        isClosed?: boolean;
    };
}

const Reviews: FC<ReviewsProps> = ({ config: { description, isClosed } }) => {
    const [reviews, setReviews] = useState<ReviewModel[]>();
    const [activeReview, setActiveReview] = useState<ReviewModel>();
    const { t } = useTranslation('pages/home');
    const modalRef = createRef<ModalElement>();

    useQuery(GET_REVIEWS_GQL, {
        variables: {
            page: 0,
            size: 50,
        },
        onCompleted: ({
            internsFeedbacks: {
                get: { items },
            },
        }) => {
            setReviews(items);
        },
    });

    if (!reviews || reviews.length === 0) {
        return null;
    }

    const CAROUSEL_SETTINGS: Settings = {
        dots: true,
        speed: 500,
        slidesToShow: 3,
        slidesToScroll: 3,
        nextArrow: <Icon name={'ArrowRightThin'} color={'black'} size={50} />,
        prevArrow: <Icon name={'ArrowLeftThin'} color={'black'} size={50} />,
        responsive: [
            {
                breakpoint: 1024,
                settings: {
                    slidesToShow: 2,
                    slidesToScroll: 2,
                    arrows: false,
                },
            },
            {
                breakpoint: 640,
                settings: {
                    slidesToShow: 1,
                    slidesToScroll: 1,
                },
            },
        ],
        appendDots: (dots) => (
            <DotsContainer>
                <DotsList> {dots} </DotsList>
            </DotsContainer>
        ),
    };

    return (
        <>
            <Container id="reviews">
                <Bubble9 src={process.env.PUBLIC_URL + `/assets/images/bubble9.png`} alt="Safeboard bubble image" />
                {isClosed && (
                    <Bubble8 src={process.env.PUBLIC_URL + `/assets/images/bubble8.png`} alt="Safeboard bubble image" />
                )}

                {!isClosed && <Coral src={process.env.PUBLIC_URL + '/assets/images/coral.png'} alt="Coral" />}

                <BaseWrapper>
                    <DescriptionContainer>
                        <Description dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(description) }} />
                    </DescriptionContainer>

                    <SliderContainer>
                        <Slider {...CAROUSEL_SETTINGS} ref={(slider) => slider}>
                            {reviews.map((review: ReviewModel) => (
                                <Slide key={review.id}>
                                    <Review review={review} isImpression />
                                    <Action
                                        onClick={() => {
                                            setActiveReview(review);
                                            modalRef?.current?.open();
                                        }}
                                    >
                                        <ActionText>{t('read-review')}</ActionText>
                                        <Icon name={'ArrowRightThin'} color={'primary'} size={20} />
                                    </Action>
                                </Slide>
                            ))}
                        </Slider>
                    </SliderContainer>
                    <PersonalDataDisclaimer variant="p3">{t('personal-data')}</PersonalDataDisclaimer>
                </BaseWrapper>
            </Container>

            <PrimaryModal atSelector="reviewModalSelector" ref={modalRef}>
                {activeReview && <Review autoHeight review={activeReview} />}
            </PrimaryModal>
        </>
    );
};

export default Reviews;
